/**
 * AdminWrapper Component
 * Main wrapper for admin pages with sidebar navigation
 */

import React, { Component } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

class AdminWrapper extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isAdmin: false,
            role: null, // 'admin' | 'sales'
            loading: true,
            error: null,
            sidebarCollapsed: false
        };
    }

    componentDidMount() {
        this.checkAdminAccess();
    }

    checkAdminAccess = async () => {
        try {
            // Role-aware entry: 'admin' (full) or 'sales' (deals only). The
            // deals access check accepts both; the old /admin/status is
            // full-admin only and would bounce a sales manager at the door.
            const response = await adminService.checkDealsAccess();
            const role = response.statusCode === 1 ? response.responseData?.role : null;
            if (role === 'admin' || role === 'sales') {
                this.setState({ isAdmin: true, role, loading: false });
                if (role === 'sales' && !this.props.location.pathname.startsWith('/beu-admin-x7k9/deals')) {
                    this.props.history.replace('/beu-admin-x7k9/deals');
                }
            } else {
                this.setState({ 
                    isAdmin: false, 
                    loading: false, 
                    error: 'You do not have admin access' 
                });
                // Redirect to dashboard after 2 seconds
                setTimeout(() => {
                    this.props.history.push('/dashboard/account');
                }, 2000);
            }
        } catch (error) {
            console.error('Admin access check failed:', error);
            this.setState({ 
                isAdmin: false, 
                loading: false, 
                error: 'Failed to verify admin access' 
            });
            setTimeout(() => {
                this.props.history.push('/dashboard/account');
            }, 2000);
        }
    }

    toggleSidebar = () => {
        this.setState(prevState => ({
            sidebarCollapsed: !prevState.sidebarCollapsed
        }));
    }

    handleLogout = () => {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('salonId');
        this.props.history.push('/');
    }

    isActiveRoute = (path) => {
        return this.props.location.pathname === path;
    }

    render() {
        const { loading, error, isAdmin, role, sidebarCollapsed } = this.state;
        const { children } = this.props;

        if (loading) {
            return (
                <div className="admin-loading">
                    <div className="admin-loading-spinner"></div>
                    <p>Verifying admin access...</p>
                </div>
            );
        }

        if (error || !isAdmin) {
            return (
                <div className="admin-error">
                    <div className="admin-error-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="64" height="64">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="12" />
                            <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                    </div>
                    <h2>Access Denied</h2>
                    <p>{error || 'You do not have permission to access this area.'}</p>
                    <p className="admin-error-redirect">Redirecting to your account...</p>
                </div>
            );
        }

        return (
            <div className={`admin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                {/* Sidebar */}
                <aside className="admin-sidebar">
                    <div className="admin-sidebar-header">
                        <Link to={role === 'sales' ? '/beu-admin-x7k9/deals' : '/beu-admin-x7k9/dashboard'} className="admin-logo">
                            <span className="admin-logo-icon">⚙️</span>
                            {!sidebarCollapsed && <span className="admin-logo-text">Beu Admin</span>}
                        </Link>
                        <button className="admin-sidebar-toggle" onClick={this.toggleSidebar}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                {sidebarCollapsed ? (
                                    <polyline points="9 18 15 12 9 6" />
                                ) : (
                                    <polyline points="15 18 9 12 15 6" />
                                )}
                            </svg>
                        </button>
                    </div>

                    <nav className="admin-nav">
                        {/* Deals — the ONE surface a sales login sees; admins see it too */}
                        <Link
                            to="/beu-admin-x7k9/deals"
                            className={`admin-nav-item ${this.props.location.pathname.startsWith('/beu-admin-x7k9/deals') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M22 2L11 13" />
                                <path d="M22 2l-7 20-4-9-9-4z" />
                            </svg>
                            {!sidebarCollapsed && <span>Deals</span>}
                        </Link>

                        {role === 'admin' && (<>
                        <Link
                            to="/beu-admin-x7k9/dashboard"
                            className={`admin-nav-item ${this.isActiveRoute('/beu-admin-x7k9/dashboard') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <rect x="3" y="3" width="7" height="7" />
                                <rect x="14" y="3" width="7" height="7" />
                                <rect x="14" y="14" width="7" height="7" />
                                <rect x="3" y="14" width="7" height="7" />
                            </svg>
                            {!sidebarCollapsed && <span>Dashboard</span>}
                        </Link>

                        <Link 
                            to="/beu-admin-x7k9/users" 
                            className={`admin-nav-item ${this.isActiveRoute('/beu-admin-x7k9/users') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                            </svg>
                            {!sidebarCollapsed && <span>Users</span>}
                        </Link>

                        <Link 
                            to="/beu-admin-x7k9/professionals" 
                            className={`admin-nav-item ${this.isActiveRoute('/beu-admin-x7k9/professionals') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                            {!sidebarCollapsed && <span>Professionals</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/businesses"
                            className={`admin-nav-item ${this.isActiveRoute('/beu-admin-x7k9/businesses') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                <polyline points="9 22 9 12 15 12 15 22" />
                            </svg>
                            {!sidebarCollapsed && <span>Businesses</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/bookings"
                            className={`admin-nav-item ${this.isActiveRoute('/beu-admin-x7k9/bookings') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                            </svg>
                            {!sidebarCollapsed && <span>Bookings</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/financials"
                            className={`admin-nav-item ${this.props.location.pathname.startsWith('/beu-admin-x7k9/financials') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <line x1="12" y1="1" x2="12" y2="23" />
                                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                            </svg>
                            {!sidebarCollapsed && <span>Financials</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/ai"
                            className={`admin-nav-item ${this.props.location.pathname.startsWith('/beu-admin-x7k9/ai') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <rect x="4" y="4" width="16" height="16" rx="2" />
                                <rect x="9" y="9" width="6" height="6" />
                                <line x1="9" y1="1" x2="9" y2="4" /><line x1="15" y1="1" x2="15" y2="4" />
                                <line x1="9" y1="20" x2="9" y2="23" /><line x1="15" y1="20" x2="15" y2="23" />
                                <line x1="20" y1="9" x2="23" y2="9" /><line x1="20" y1="14" x2="23" y2="14" />
                                <line x1="1" y1="9" x2="4" y2="9" /><line x1="1" y1="14" x2="4" y2="14" />
                            </svg>
                            {!sidebarCollapsed && <span>AI</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/merchant-approvals"
                            className={`admin-nav-item ${this.isActiveRoute('/beu-admin-x7k9/merchant-approvals') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M9 12l2 2 4-4" />
                                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
                                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
                                <path d="M3 5c0 1.66 4 3 9 3s9-1.34 9-3-4-3-9-3-9 1.34-9 3" />
                            </svg>
                            {!sidebarCollapsed && <span>Approvals</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/reconciliations"
                            className={`admin-nav-item ${this.props.location.pathname.startsWith('/beu-admin-x7k9/reconciliations') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                                <path d="M21 3v6h-6" />
                            </svg>
                            {!sidebarCollapsed && <span>Reconciliations</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/payment-bible"
                            className={`admin-nav-item ${this.props.location.pathname.startsWith('/beu-admin-x7k9/payment-bible') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                            </svg>
                            {!sidebarCollapsed && <span>Payment Bible</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/policies"
                            className={`admin-nav-item ${this.props.location.pathname.startsWith('/beu-admin-x7k9/policies') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                            </svg>
                            {!sidebarCollapsed && <span>Policies</span>}
                        </Link>

                        <Link
                            to="/beu-admin-x7k9/bailey"
                            className={`admin-nav-item ${this.isActiveRoute('/beu-admin-x7k9/bailey') ? 'active' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                            </svg>
                            {!sidebarCollapsed && <span>Bailey 🦦</span>}
                        </Link>
                        </>)}
                    </nav>

                    <div className="admin-sidebar-footer">
                        <Link to="/dashboard/account" className="admin-nav-item admin-back-link">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <line x1="19" y1="12" x2="5" y2="12" />
                                <polyline points="12 19 5 12 12 5" />
                            </svg>
                            {!sidebarCollapsed && <span>Back to Client</span>}
                        </Link>

                        <button className="admin-nav-item admin-logout-btn" onClick={this.handleLogout}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                <polyline points="16 17 21 12 16 7" />
                                <line x1="21" y1="12" x2="9" y2="12" />
                            </svg>
                            {!sidebarCollapsed && <span>Logout</span>}
                        </button>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="admin-main">
                    {children}
                </main>
            </div>
        );
    }
}

function mapStateToProps(state) {
    return {
        user: state.user?.userInfo
    };
}

export default withRouter(connect(mapStateToProps)(AdminWrapper));
